import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

import { DataTableComponent, TableColumn } from
  '@shared/components/data-table/data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import type { UserBusinessUnitAssignmentItem } from
  '@app/api/user-service/model/userBusinessUnitAssignmentItem';
import { EmployeeMgmtService } from
  '../../services/employee-mgmt.service';

export interface UnassignRolesDialogData {
  selectedUserIds: string[];
  /** Union of role names assigned to the selected users. */
  assignedRoleNames: string[];
  /** Per-role list of user display names (who has that role). If set, roles tab shows users assigned. */
  assignedRolesWithUsers?: { roleName: string; usersDisplay: string }[];
  /** Optional id -> display name for selected users (e.g. username). */
  userDisplays?: { id: string; display: string }[];
}

export interface UnassignRolesDialogResult {
  roleNames?: string[];
  /** One entry per BU to unassign; one API call per entry. */
  businessUnits?: { businessUnitId: number; userIds: string[] }[];
}

export interface UnassignRoleRow {
  name: string;
  usersDisplay: string;
}

/** One BU with the selected user IDs assigned to it (for unassign BU tab). */
export interface BuUnassignRow {
  id: number;
  businessUnitId: number;
  businessUnitName: string;
  userIds: string[];
  usersDisplay: string;
}

const UNASSIGN_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'configuration.user_roles_assignment.dialog.role_name',
    prop: 'name',
    width: 220,
  },
  {
    label: 'configuration.user_roles_assignment.dialog.users_assigned',
    prop: 'usersDisplay',
    width: 320,
    styleClass: 'users-assigned-cell-wrap',
  },
];

const UNASSIGN_BU_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'configuration.user_roles_assignment.dialog.bu_name',
    prop: 'businessUnitName',
    width: 220,
  },
  {
    label: 'configuration.user_roles_assignment.dialog.users_assigned',
    prop: 'usersDisplay',
    width: 320,
    styleClass: 'users-assigned-cell-wrap',
  },
];

@Component({
  selector: 'app-unassign-roles-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DataTableComponent,
    AppButtonComponent,
  ],
  templateUrl: './unassign-roles-dialog.component.html',
  styleUrl: './unassign-roles-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnassignRolesDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly userRolesAssignmentService = inject(
    EmployeeMgmtService,
  );

  readonly columns = UNASSIGN_TABLE_COLUMNS;
  readonly buColumns = UNASSIGN_BU_TABLE_COLUMNS;
  readonly roles = signal<UnassignRoleRow[]>([]);
  readonly selectedRoles = signal<UnassignRoleRow[]>([]);

  readonly activeTab = signal<'roles' | 'bu'>('roles');
  readonly buItems = signal<BuUnassignRow[]>([]);
  readonly selectedBuItems = signal<BuUnassignRow[]>([]);
  readonly buLoading = signal(false);

  constructor() {
    const data = this.config.data as UnassignRolesDialogData | undefined;
    const withUsers = Array.isArray(data?.assignedRolesWithUsers)
      ? data.assignedRolesWithUsers
      : [];
    if (withUsers.length > 0) {
      this.roles.set(
        withUsers.map((r) => ({ name: r.roleName, usersDisplay: r.usersDisplay })),
      );
    } else {
      const names = Array.isArray(data?.assignedRoleNames)
        ? data.assignedRoleNames
        : [];
      this.roles.set(
        names.map((name) => ({ name, usersDisplay: '' })),
      );
    }
    this.loadBuAssignments();
  }

  setTab(tab: 'roles' | 'bu'): void {
    this.activeTab.set(tab);
  }

  private loadBuAssignments(): void {
    const data = this.config.data as UnassignRolesDialogData | undefined;
    const userIds = Array.isArray(data?.selectedUserIds)
      ? data.selectedUserIds
      : [];
    const userDisplays = Array.isArray(data?.userDisplays)
      ? data.userDisplays
      : [];
    const displayMap = new Map(
      userDisplays.map((u) => [u.id, u.display]),
    );
    if (userIds.length === 0) {
      this.buItems.set([]);
      return;
    }
    this.buLoading.set(true);
    const requests = userIds.map((userId) =>
      this.userRolesAssignmentService.getAssignmentsForUser(userId).pipe(
        map((res) =>
          (res.assignments ?? []).map((a: UserBusinessUnitAssignmentItem) => ({
            userId,
            buId: a.businessUnitId,
            buName: a.name ?? a.code ?? String(a.businessUnitId),
          })),
        ),
      ),
    );
    forkJoin(requests).subscribe({
      next: (results) => {
        const byBu = new Map<
          number,
          { name: string; userIds: string[] }
        >();
        for (const list of results) {
          for (const { userId, buId, buName } of list) {
            if (buId == null) continue;
            const key = Number(buId);
            const cur = byBu.get(key);
            if (cur) {
              if (!cur.userIds.includes(userId)) {
                cur.userIds.push(userId);
              }
            } else {
              byBu.set(key, { name: buName, userIds: [userId] });
            }
          }
        }
        const rows: BuUnassignRow[] = Array.from(byBu.entries()).map(
          ([id, { name, userIds: uids }]) => ({
            id,
            businessUnitId: id,
            businessUnitName: name,
            userIds: uids,
            usersDisplay: uids
              .map((uid) => displayMap.get(uid) ?? uid)
              .join(', '),
          }),
        );
        this.buItems.set(rows);
        this.buLoading.set(false);
      },
      error: () => this.buLoading.set(false),
    });
  }

  onSelectionChange(selection: UnassignRoleRow[]): void {
    this.selectedRoles.set(selection);
  }

  onBuSelectionChange(selection: BuUnassignRow[]): void {
    this.selectedBuItems.set(selection);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  canConfirm(): boolean {
    return (
      this.selectedRoles().length > 0 ||
      this.selectedBuItems().length > 0
    );
  }

  onConfirm(): void {
    const result: UnassignRolesDialogResult = {};

    const roleNames = this.selectedRoles()
      .map((r) => r?.name)
      .filter((name): name is string => !!name);
    if (roleNames.length > 0) {
      result.roleNames = roleNames;
    }

    const selectedBu = this.selectedBuItems();
    if (selectedBu.length > 0) {
      result.businessUnits = selectedBu.map((row) => ({
        businessUnitId: row.businessUnitId,
        userIds: row.userIds,
      }));
    }

    this.dialogRef.close(result);
  }
}
